import Phaser from 'phaser';
import type { MorseSymbol } from '@/utils/morse';
import { decodePlayerInput } from '@/utils/morse';
import { THEME } from '@/config/theme';
import { MORSE_SPACING } from '@/utils/timing';

interface LetterGroup {
  symbols: Phaser.GameObjects.Rectangle[];
  symbolInputs: MorseSymbol[];
  letter: Phaser.GameObjects.Text | null;
}

export interface SentTrackConfig {
  hitZoneX: number;
  trackY: number;
  letterY: number;
  scrollSpeed: number;
  fadeEndX: number;
  stopX: number;  // X position where letters stop and accumulate
  mask?: Phaser.Display.Masks.GeometryMask;  // Optional mask for clipping symbols
}

export class SentTrack {
  private scene: Phaser.Scene;
  private config: SentTrackConfig;

  private groups: LetterGroup[] = [];
  private currentInputs: MorseSymbol[] = [];
  private currentSymbols: Phaser.GameObjects.Rectangle[] = [];
  private decodedMessage: string = '';
  private stoppedLetters: Phaser.GameObjects.Text[] = [];
  private lastInputTime: number = 0;
  private lastSymbol: MorseSymbol | null = null;

  constructor(scene: Phaser.Scene, config: SentTrackConfig) {
    this.scene = scene;
    this.config = config;
  }

  private getLetterGapMs(): number {
    const symbolWidth = this.lastSymbol === '.' ? MORSE_SPACING.dotWidth : MORSE_SPACING.dashWidth;
    return ((symbolWidth + MORSE_SPACING.letterGap) / this.config.scrollSpeed) * 1000;
  }

  start(): void {
    this.decodedMessage = '';
    this.currentInputs = [];
    this.currentSymbols = [];
    this.groups = [];
    this.stoppedLetters = [];
    this.lastInputTime = 0;
    this.lastSymbol = null;
  }

  addSymbol(symbol: MorseSymbol, time: number): void {
    this.currentInputs.push(symbol);
    this.lastInputTime = time;
    this.lastSymbol = symbol;

    // Create visual symbol at left edge of hit zone - use yellow to differentiate from incoming cyan notes
    const width = symbol === '.' ? MORSE_SPACING.dotWidth : MORSE_SPACING.dashWidth;
    const hitZoneLeftEdge = this.config.hitZoneX - 30;  // Hit zone is 60px wide, centered on hitZoneX
    const rect = this.scene.add.rectangle(
      hitZoneLeftEdge,
      this.config.trackY,
      width,
      MORSE_SPACING.noteHeight,
      THEME.colors.yellowHex
    );
    rect.setOrigin(0, 0.5);
    rect.setStrokeStyle(2, THEME.colors.backgroundPanelHex);

    // Apply mask to clip symbols at track boundaries
    if (this.config.mask) {
      rect.setMask(this.config.mask);
    }

    this.currentSymbols.push(rect);
  }

  private finalizeCharacter(): void {
    if (this.currentInputs.length === 0) return;

    // Decode
    const char = decodePlayerInput(this.currentInputs);
    this.decodedMessage += char;

    // Find center of current symbols
    let sumX = 0;
    for (const s of this.currentSymbols) {
      sumX += s.x;
    }
    const centerX = sumX / this.currentSymbols.length;

    // Create letter above symbols - use yellow to match sent symbols
    const letter = this.scene.add.text(centerX, this.config.letterY, char, {
      fontFamily: THEME.fonts.mono,
      fontSize: THEME.sizes.heading,
      color: THEME.colors.yellow,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    // Save group
    this.groups.push({
      symbols: this.currentSymbols,
      symbolInputs: this.currentInputs,
      letter: letter,
    });

    // Reset for next character
    this.currentInputs = [];
    this.currentSymbols = [];
  }

  forceFinalize(): void {
    this.finalizeCharacter();
  }

  update(time: number, delta: number): void {
    const dx = this.config.scrollSpeed * (delta / 1000);

    // Check if enough time has passed to finalize current character
    if (this.currentInputs.length > 0 && this.lastInputTime > 0) {
      const elapsed = time - this.lastInputTime;
      if (elapsed >= this.getLetterGapMs()) {
        this.finalizeCharacter();
      }
    }

    // Move current symbols (not yet finalized)
    for (const symbol of this.currentSymbols) {
      symbol.x -= dx;
    }

    // Update groups
    for (let i = this.groups.length - 1; i >= 0; i--) {
      const group = this.groups[i];

      // Move symbols
      for (let j = group.symbols.length - 1; j >= 0; j--) {
        const symbol = group.symbols[j];
        symbol.x -= dx;

        // Fade at left boundary
        if (symbol.x < this.config.fadeEndX) {
          symbol.alpha -= 0.1;
          if (symbol.alpha <= 0) {
            symbol.destroy();
            group.symbols.splice(j, 1);
          }
        }
      }

      // Move letter
      if (group.letter) {
        group.letter.x -= dx;

        // Stop at accumulation point
        const nextStopX = this.config.stopX + this.stoppedLetters.length * 20;
        if (group.letter.x <= nextStopX) {
          group.letter.x = nextStopX;
          this.stoppedLetters.push(group.letter);
          group.letter = null;  // Detach from group
        }
      }

      // Remove empty groups
      if (group.symbols.length === 0 && !group.letter) {
        this.groups.splice(i, 1);
      }
    }
  }

  isEmpty(): boolean {
    return this.currentSymbols.length === 0 && this.groups.every(g => g.symbols.length === 0);
  }

  getDecodedMessage(): string {
    return this.decodedMessage;
  }

  destroy(): void {
    for (const s of this.currentSymbols) s.destroy();
    for (const g of this.groups) {
      for (const s of g.symbols) s.destroy();
      g.letter?.destroy();
    }
    for (const l of this.stoppedLetters) l.destroy();
    this.currentSymbols = [];
    this.groups = [];
    this.stoppedLetters = [];
    this.decodedMessage = '';
  }
}
